/**
 * Layer 4 — Platform Services. Technical services powering the app. These are
 * the leaves of the dependency DAG; most capabilities `dependsOn` one or more.
 * Disabling a service produces the largest impact set (see dependencyGraph).
 *
 * Services depend only on other (lower-level) services to keep the graph acyclic.
 */
import { MaturityLevel, definePlatformService } from '@/lib/capabilities/contracts';

export const platformServices = {
  'services.auth': definePlatformService({
    key: 'services.auth',
    layer: 'platform_service',
    title: 'Authentication',
    description: 'Session, identity and sign-in. Foundational; nothing runs without it.',
    dependsOn: [],
    provides: ['identity'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'core',
  }),
  'services.permissions': definePlatformService({
    key: 'services.permissions',
    layer: 'platform_service',
    title: 'Permissions',
    description: 'Role and membership authority. Authoritative for access; the CMP never overrides it.',
    dependsOn: ['services.auth'],
    provides: ['authorization'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'core',
  }),
  'services.audit': definePlatformService({
    key: 'services.audit',
    alwaysIncluded: 'Core: the audit trail is always kept.',
    layer: 'platform_service',
    title: 'Audit',
    description: 'Immutable activity and change history across the platform.',
    dependsOn: ['services.auth'],
    provides: ['audit_trail'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'core',
  }),
  'services.storage': definePlatformService({
    key: 'services.storage',
    layer: 'platform_service',
    title: 'Storage',
    description: 'File and media storage (photos, documents, reports).',
    dependsOn: ['services.auth'],
    provides: ['file_storage'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'storage',
  }),
  'services.search': definePlatformService({
    key: 'services.search',
    layer: 'platform_service',
    title: 'Search',
    description: 'Cross-entity search and lookup.',
    dependsOn: ['services.auth'],
    provides: ['search'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'core',
  }),
  'services.offline_sync': definePlatformService({
    key: 'services.offline_sync',
    alwaysIncluded: 'Core: changes made offline always sync.',
    layer: 'platform_service',
    title: 'Offline Synchronisation',
    description: 'Write outbox and offline-first reconciliation.',
    dependsOn: ['services.auth'],
    provides: ['offline_sync'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'sync_outbox',
  }),
  'services.notification_engine': definePlatformService({
    key: 'services.notification_engine',
    layer: 'platform_service',
    title: 'Notification Engine',
    description: 'Toast, push and email notification delivery.',
    dependsOn: ['services.auth'],
    provides: ['notifications'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'push',
  }),
  'services.reporting_engine': definePlatformService({
    key: 'services.reporting_engine',
    layer: 'platform_service',
    title: 'Reporting Engine',
    description: 'Report generation, branding and export pipeline.',
    dependsOn: ['services.storage'],
    provides: ['reporting'],
    requires: ['file_storage'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['core'],
    active: true,
    serviceKind: 'core',
  }),
  'services.ai_platform': definePlatformService({
    key: 'services.ai_platform',
    layer: 'platform_service',
    title: 'AI Platform',
    description: 'Edge-function AI invocation, metering and credit gating.',
    dependsOn: ['services.auth'],
    provides: ['ai_inference'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.mlkit': definePlatformService({
    key: 'services.mlkit',
    layer: 'platform_service',
    title: 'ML Kit',
    description: 'On-device barcode / label / text recognition.',
    dependsOn: ['services.auth'],
    provides: ['on_device_ml'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'on_device'],
    active: true,
    serviceKind: 'core',
  }),
  'services.work_intelligence': definePlatformService({
    key: 'services.work_intelligence',
    layer: 'platform_service',
    title: 'Work intelligence',
    description: 'Shared work-evidence analysis facade over AI + on-device extractors.',
    dependsOn: ['services.ai_platform', 'services.mlkit'],
    provides: ['work_intelligence'],
    requires: ['ai_inference', 'on_device_ml'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.evidence_intelligence': definePlatformService({
    key: 'services.evidence_intelligence',
    layer: 'platform_service',
    title: 'Evidence intelligence',
    description: 'Local-first evidence analysis (dates, meters, compliance) with AI escalation.',
    dependsOn: ['services.mlkit'],
    provides: ['evidence_intelligence'],
    requires: ['on_device_ml'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'on_device'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.journey_analytics_engine': definePlatformService({
    key: 'services.journey_analytics_engine',
    layer: 'platform_service',
    title: 'Journey Analytics Engine',
    description: 'Portfolio journey-flow intelligence and analytics rollups.',
    dependsOn: ['services.reporting_engine'],
    provides: ['journey_analytics'],
    requires: ['reporting'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['analytics'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.automation_engine': definePlatformService({
    key: 'services.automation_engine',
    layer: 'platform_service',
    title: 'Automation Engine',
    description: 'Scheduled jobs, recurring work and workflow automation metering.',
    dependsOn: ['services.auth'],
    provides: ['automation'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['automation'],
    active: true,
    serviceKind: 'cron',
  }),
  'services.integration_engine': definePlatformService({
    key: 'services.integration_engine',
    layer: 'platform_service',
    title: 'Integration Engine',
    description: 'Inbound/outbound data integration and import pipelines.',
    dependsOn: ['services.auth'],
    provides: ['integration'],
    requires: ['identity'],
    // The Matrix has no row for it on any plan (modules.integrations above it is the gate).
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.api_platform': definePlatformService({
    key: 'services.api_platform',
    layer: 'platform_service',
    title: 'API Platform',
    description: 'First-party public API gateway (REST surface).',
    dependsOn: ['services.auth'],
    provides: ['public_api'],
    requires: ['identity'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.graphql_platform': definePlatformService({
    key: 'services.graphql_platform',
    layer: 'platform_service',
    title: 'GraphQL Platform',
    description: 'GraphQL surface over the public API gateway.',
    dependsOn: ['services.api_platform'],
    provides: ['graphql'],
    requires: ['public_api'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.webhook_platform': definePlatformService({
    key: 'services.webhook_platform',
    layer: 'platform_service',
    title: 'Webhook platform',
    description: 'Signed outbound webhooks via the integration gateway.',
    dependsOn: ['services.integration_engine'],
    provides: ['webhooks'],
    requires: ['integration'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    serviceKind: 'edge_function',
  }),
  'services.mcp_platform': definePlatformService({
    key: 'services.mcp_platform',
    layer: 'platform_service',
    title: 'MCP Platform',
    description: 'Model Context Protocol bridge over the public API gateway.',
    dependsOn: ['services.api_platform'],
    provides: ['mcp'],
    requires: ['public_api'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    serviceKind: 'edge_function',
  }),
} as const;
